import api from '../lib/api';
import { IAccountDataResponse, IAccountList } from '@tool-no-hu/shared/types';
import { showToast } from '../context/ToastContext';

export async function getAccountsService(
  type = 'LIST_DATA',
  options: { silent?: boolean } = {},
): Promise<IAccountList[] | IAccountDataResponse[]> {
  try {
    const response = await api.get('/api/v1/accounts?type=' + type, {
      _skipToast: options.silent,
    } as any);
    if (response.data && response.data.code === 200) {
      const list = response.data.data?.accounts || [];
      return list;
    }
    return [];
  } catch (error) {
    console.error('Failed to get accounts:', error);
    return [];
  }
}

/** Ném lỗi ra ngoài để caller hiển thị message của BE (vd: account đang chạy). */
export async function deleteAccountService(id: string): Promise<boolean> {
  const response = await api.delete(`/api/v1/accounts/${id}`);
  return response.data?.code === 200;
}

export interface PaginatedAccounts {
  items: IAccountDataResponse[];
  total: number;
  totalPages: number;
  page: number;
  limit: number;
}

export async function getAccountsPaginatedService(
  page: number,
  limit: number,
  search?: string,
): Promise<PaginatedAccounts | null> {
  try {
    const response = await api.get('/api/v1/accounts/paginated', {
      params: { page, limit, search: search || undefined },
    });
    if (response.data && response.data.code === 200) {
      return response.data.data;
    }
    return null;
  } catch (error) {
    console.error('Failed to fetch paginated accounts:', error);
    return null;
  }
}

export interface AccountCredentials {
  _id: string;
  username: string;
  displayname: string;
  password: string;
  proxy: string;
}

/** Thông tin đăng nhập của 1 account — chỉ fetch khi mở modal, không nằm trong list. */
export async function getAccountCredentialsService(
  id: string,
): Promise<AccountCredentials | null> {
  try {
    const response = await api.get(`/api/v1/accounts/${id}/credentials`);
    if (response.data && response.data.code === 200) {
      return response.data.data;
    }
    return null;
  } catch (error) {
    console.error('Failed to fetch account credentials:', error);
    return null;
  }
}

export async function updateAccountProxyService(
  id: string,
  proxy: string,
): Promise<IAccountDataResponse | null> {
  try {
    const response = await api.put(`/api/v1/accounts/${id}/proxy`, { proxy });
    if (response.data && response.data.code === 200) {
      return response.data.data;
    }
    return null;
  } catch (error) {
    console.error('Failed to update proxy:', error);
    return null;
  }
}

export interface BulkCreateResult {
  proxy: string;
  success: boolean;
  username?: string;
  password?: string;
  displayname?: string;
  error?: string;
}

export interface RandomAccountResult {
  _id: string;
  username: string;
  displayname: string;
  /** Mật khẩu random do server sinh — chỉ trả về đúng lần tạo này */
  password: string;
  proxy: string;
}

export interface LoginAndAddResult {
  _id: string;
  username: string;
  displayname: string;
  proxy: string;
}

/**
 * Tạo tài khoản Sunwin mới — username + tên hiển thị do user nhập,
 * mật khẩu do server sinh ngẫu nhiên.
 * Lưu local DB, KHÔNG login — user tự vào Sảnh login.
 */
export async function createRandomAccountService(data: {
  username: string;
  displayName: string;
  proxy: string;
}): Promise<RandomAccountResult | null> {
  try {
    const response = await api.post('/api/v1/accounts/random', data);
    if (response.data && response.data.code === 201) {
      return response.data.data;
    }
    return null;
  } catch (error: any) {
    console.error('Failed to create random account:', error);
    const msg = error?.response?.data?.message || 'Tạo tài khoản thất bại';
    showToast.error(msg);
    return null;
  }
}

/**
 * Validate Sunwin credentials bằng cách gọi login API.
 * Nếu thành công → lưu local DB, KHÔNG lưu tokens.
 */
export async function loginAndAddAccountService(data: {
  username: string;
  password: string;
  proxy: string;
}): Promise<LoginAndAddResult | null> {
  try {
    const response = await api.post('/api/v1/accounts/login', data);
    if (response.data && response.data.code === 201) {
      return response.data.data;
    }
    return null;
  } catch (error: any) {
    console.error('Failed to login and add account:', error);
    const msg = error?.response?.data?.message || 'Thêm tài khoản thất bại';
    showToast.error(msg);
    return null;
  }
}

export async function bulkCreateAccountsService(
  quantity: number,
  proxies: string[]
): Promise<BulkCreateResult[]> {
  try {
    const response = await api.post('/api/v1/accounts/bulk-create', {
      quantity,
      proxies,
    });
    if (response.data && response.data.code === 200) {
      return response.data.data?.results || [];
    }
    return [];
  } catch (error: any) {
    console.error('Failed bulk create accounts:', error);
    throw new Error(error?.response?.data?.message || 'Tạo tài khoản hàng loạt thất bại');
  }
}
